import { create } from 'zustand';
import { DateTime } from 'luxon';
import { OverviewDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/store/overviewDashboard.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import {
  TotalShiftsResponse,
  ShiftBreakdownResponse,
  ShiftBreakdownCategory,
  TrendDataPoint,
  TrendGranularity,
  FulfillmentByPositionResponseItem,
  FulfillmentByPositionResponse,
  PositionFulfillment,
  CalloutVolumeByLocationResponseItem,
  CalloutVolumeByLocationResponse,
  LocationCallOut,
  FulfillmentTrendResponse,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.types';
import {
  SHIFT_STATUS_CATEGORIES,
  ShiftStatusKey,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.constants';
import { toNumber } from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import { hasDataLockRejection } from 'pages/ReportsPage/store/reportsDataHealth.store';
import { OVERVIEW_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/store/overviewDashboard.stub';
import {
  DASHBOARDS_SHIFTS_TOTAL,
  DASHBOARDS_SHIFTS_BREAKDOWN,
  DASHBOARDS_SHIFTS_CALLOUT_HOURS,
  DASHBOARDS_SHIFTS_FULFILLMENT_PERCENTAGE,
  DASHBOARDS_SHIFTS_FULFILLMENT_BY_POSITION,
  DASHBOARDS_SHIFTS_CALLOUT_VOLUME_BY_LOCATION,
  DASHBOARDS_TEAM_MEMBERS_TOTAL_ACTIVE,
  DASHBOARDS_SHIFTS_FULFILLMENT_TREND,
} from 'constants/endpoints';
import { getThresholdLevel } from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.constants';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

function mapResponseToBreakdown(response: ShiftBreakdownResponse): ShiftBreakdownCategory[] {
  return SHIFT_STATUS_CATEGORIES.map(({ key, i18nKey }) => ({
    category: i18nKey,
    count: response[key as ShiftStatusKey] ?? 0,
    percentage: toNumber(response[`${key}_prct` as keyof ShiftBreakdownResponse]),
  }));
}

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

function mapFulfillmentTrend(response: FulfillmentTrendResponse): TrendDataPoint[] {
  return response.buckets.map((bucket) => ({
    date: bucket.bucket_start?.split(' ')[0] ?? '',
    totalShifts: bucket.total_shifts,
    fulfillmentPercentage: toNumber(bucket.fulfillment_pct),
  }));
}

function granularityStep(
  granularity: string,
): { days: number } | { weeks: number } | { months: number } {
  const normalized = (granularity ?? '').toLowerCase();
  if (normalized.startsWith('week')) return { weeks: 1 };
  if (normalized.startsWith('month')) return { months: 1 };
  return { days: 1 };
}

function toDateKey(iso: string): string {
  const parsed = DateTime.fromISO(iso, { setZone: true });
  return parsed.isValid ? parsed.toFormat('yyyy-MM-dd') : '';
}

function fillTrendGaps(
  points: TrendDataPoint[],
  dateRange: [string, string],
  granularity: string,
): TrendDataPoint[] {
  const startKey = toDateKey(dateRange[0]);
  const endKey = toDateKey(dateRange[1]);
  if (!startKey || !endKey || endKey < startKey) return points;

  const step = granularityStep(granularity);
  const pointsByDate = new Map(points.map((point) => [point.date, point]));
  const earliestKey = points.reduce(
    (earliest, point) => (point.date && point.date < earliest ? point.date : earliest),
    points[0]?.date ?? startKey,
  );

  let cursor = DateTime.fromFormat(earliestKey, 'yyyy-MM-dd');
  if (!cursor.isValid) return points;

  while (cursor.toFormat('yyyy-MM-dd') > startKey) {
    cursor = cursor.minus(step);
  }

  const filled: TrendDataPoint[] = [];
  let key = cursor.toFormat('yyyy-MM-dd');
  while (key <= endKey) {
    filled.push(pointsByDate.get(key) ?? { date: key, totalShifts: 0, fulfillmentPercentage: 0 });
    cursor = cursor.plus(step);
    key = cursor.toFormat('yyyy-MM-dd');
  }

  return filled;
}

function mapFulfillmentByPosition(
  response: FulfillmentByPositionResponseItem[],
): PositionFulfillment[] {
  return response.map((item) => {
    const fulfillmentPercentage = toNumber(item.fulfillment_pct);
    return {
      position: item.position_name,
      fulfillmentPercentage,
      totalShifts: item.fulfilled_count,
      thresholdLevel: getThresholdLevel(fulfillmentPercentage),
    };
  });
}

function mapCalloutVolumeByLocation(
  response: CalloutVolumeByLocationResponseItem[],
): LocationCallOut[] {
  return response.map((item) => ({
    locationName: item.community_name,
    locationId: String(item.community_id),
    callOutCount: item.callout_count,
  }));
}

function resolveTrendGranularity(granularity: string | undefined): TrendGranularity | null {
  const normalized = (granularity ?? '').toLowerCase();
  if (normalized.startsWith('month')) return 'monthly';
  if (normalized.startsWith('week')) return 'weekly';
  if (normalized.startsWith('da')) return 'daily';
  return null;
}

function granularityFromRange(dateRange: [string, string]): TrendGranularity {
  const startKey = toDateKey(dateRange[0]);
  const endKey = toDateKey(dateRange[1]);
  if (!startKey || !endKey) return 'daily';
  const days = DateTime.fromFormat(endKey, 'yyyy-MM-dd').diff(
    DateTime.fromFormat(startKey, 'yyyy-MM-dd'),
    'days',
  ).days;
  if (days > 180) return 'monthly';
  if (days > 45) return 'weekly';
  return 'daily';
}

const INITIAL_STATE = {
  totalShifts: 0,
  periodDays: null as number | null,
  changePrct: null as number | null,
  totalShiftsError: false,
  calloutHoursTotal: 0,
  calloutHoursPeriodDays: null as number | null,
  calloutHoursChangePrct: null as number | null,
  calloutHoursError: false,
  fulfillmentPrctTotal: 0,
  fulfillmentPrctPeriodDays: null as number | null,
  fulfillmentPrctChangePrct: null as number | null,
  fulfillmentPrctError: false,
  shiftBreakdown: [] as ShiftBreakdownCategory[],
  breakdownError: false,
  fulfillmentByPositionError: false,
  callOutByLocationError: false,
  activeTeamMembersTotal: 0,
  activeTeamMembersPeriodDays: null as number | null,
  activeTeamMembersChangePrct: null as number | null,
  activeTeamMembersError: false,
  trendError: false,
  isLoading: false,
  trendData: [] as TrendDataPoint[],
  trendGranularity: null as TrendGranularity | null,
  fulfillmentByPosition: null,
  fulfillmentByPositionPage: 1,
  fulfillmentByPositionTotalPages: 1,
  fulfillmentByPositionLoadingMore: false,
  callOutByLocation: null,
  callOutByLocationPage: 1,
  callOutByLocationTotalPages: 1,
  callOutByLocationLoadingMore: false,
};

let fetchId = 0;
let fulfillmentByPositionBaseQuery: Record<string, unknown> | null = null;
let callOutByLocationBaseQuery: Record<string, unknown> | null = null;

export const overviewDashboardStore = create<OverviewDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;
      set({
        isLoading: true,
        totalShiftsError: false,
        breakdownError: false,
        calloutHoursError: false,
        fulfillmentPrctError: false,
        fulfillmentByPositionError: false,
        callOutByLocationError: false,
        activeTeamMembersError: false,
        trendError: false,
      });

      const query = buildFilterQuery(dateRange, locationIds, positions, employmentTypes);
      fulfillmentByPositionBaseQuery = query;
      callOutByLocationBaseQuery = query;

      const totalShiftsRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_TOTAL(),
        method: 'GET',
        query,
      });

      const breakdownRequest = createDashboardsRequest<ShiftBreakdownResponse>({
        url: DASHBOARDS_SHIFTS_BREAKDOWN(),
        method: 'GET',
        query,
      });

      const calloutHoursRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_CALLOUT_HOURS(),
        method: 'GET',
        query,
      });

      const fulfillmentPrctRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_PERCENTAGE(),
        method: 'GET',
        query,
      });

      const fulfillmentByPositionRequest = fetchDashboardPage<
        FulfillmentByPositionResponse,
        FulfillmentByPositionResponseItem
      >({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_BY_POSITION(),
        query,
        page: 1,
        selectItems: (body) => body.fulfillment_by_position ?? [],
      });

      const calloutVolumeByLocationRequest = fetchDashboardPage<
        CalloutVolumeByLocationResponse,
        CalloutVolumeByLocationResponseItem
      >({
        url: DASHBOARDS_SHIFTS_CALLOUT_VOLUME_BY_LOCATION(),
        query,
        page: 1,
        selectItems: (body) => body.callout_volume_by_location ?? [],
      });

      const activeTeamMembersRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_TEAM_MEMBERS_TOTAL_ACTIVE(),
        method: 'GET',
        query,
      });

      const fulfillmentTrendRequest = createDashboardsRequest<FulfillmentTrendResponse>({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_TREND(),
        method: 'GET',
        query,
      });

      Promise.allSettled([
        totalShiftsRequest,
        breakdownRequest,
        calloutHoursRequest,
        fulfillmentPrctRequest,
        fulfillmentByPositionRequest,
        calloutVolumeByLocationRequest,
        activeTeamMembersRequest,
        fulfillmentTrendRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...OVERVIEW_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [
          totalResult,
          breakdownResult,
          calloutResult,
          fulfillmentResult,
          fulfillmentByPosResult,
          calloutByLocResult,
          activeTeamMembersResult,
          trendResult,
        ] = results;

        const totalData = unwrapDashboardRoot<TotalShiftsResponse>(
          totalResult.status === 'fulfilled' ? totalResult.value.data : null,
          'total_shifts',
        );
        const breakdownData = unwrapDashboardRoot<ShiftBreakdownResponse>(
          breakdownResult.status === 'fulfilled' ? breakdownResult.value.data : null,
          'breakdown',
        );
        const calloutData = unwrapDashboardRoot<TotalShiftsResponse>(
          calloutResult.status === 'fulfilled' ? calloutResult.value.data : null,
          'callout_hours',
        );
        const fulfillmentData = unwrapDashboardRoot<TotalShiftsResponse>(
          fulfillmentResult.status === 'fulfilled' ? fulfillmentResult.value.data : null,
          'fulfillment_percentage',
        );
        const fulfillmentByPosData =
          fulfillmentByPosResult.status === 'fulfilled' ? fulfillmentByPosResult.value : null;
        const calloutByLocData =
          calloutByLocResult.status === 'fulfilled' ? calloutByLocResult.value : null;
        const activeTeamMembersData = unwrapDashboardRoot<TotalShiftsResponse>(
          activeTeamMembersResult.status === 'fulfilled'
            ? activeTeamMembersResult.value.data
            : null,
          'total_active',
        );
        const trendResponseData = unwrapDashboardRoot<FulfillmentTrendResponse>(
          trendResult.status === 'fulfilled' ? trendResult.value.data : null,
          'fulfillment_trend',
        );

        const shiftBreakdown = breakdownData ? mapResponseToBreakdown(breakdownData) : [];
        const resolvedGranularity = resolveTrendGranularity(trendResponseData?.granularity);
        if (trendResponseData && resolvedGranularity === null) {
          console.error(
            'Overview trend response is missing a recognized granularity:',
            trendResponseData.granularity,
          );
        }
        const trendGranularity = resolvedGranularity ?? granularityFromRange(dateRange);

        set({
          totalShifts: toNumber(totalData?.current_period_total),
          periodDays: totalData?.period_days ?? null,
          changePrct: totalData?.change_prct == null ? null : toNumber(totalData.change_prct),
          totalShiftsError: totalResult.status === 'rejected',
          calloutHoursTotal: toNumber(calloutData?.current_period_total),
          calloutHoursPeriodDays: calloutData?.period_days ?? null,
          calloutHoursChangePrct:
            calloutData?.change_prct == null ? null : toNumber(calloutData.change_prct),
          calloutHoursError: calloutResult.status === 'rejected',
          fulfillmentPrctTotal: toNumber(fulfillmentData?.current_period_total),
          fulfillmentPrctPeriodDays: fulfillmentData?.period_days ?? null,
          fulfillmentPrctChangePrct:
            fulfillmentData?.change_prct == null ? null : toNumber(fulfillmentData.change_prct),
          fulfillmentPrctError: fulfillmentResult.status === 'rejected',
          shiftBreakdown,
          breakdownError: breakdownResult.status === 'rejected',
          fulfillmentByPosition: fulfillmentByPosData
            ? mapFulfillmentByPosition(fulfillmentByPosData.items)
            : null,
          fulfillmentByPositionPage: fulfillmentByPosData?.meta?.current_page ?? 1,
          fulfillmentByPositionTotalPages: fulfillmentByPosData?.meta?.total_pages ?? 1,
          fulfillmentByPositionLoadingMore: false,
          fulfillmentByPositionError: fulfillmentByPosResult.status === 'rejected',
          callOutByLocation: calloutByLocData
            ? mapCalloutVolumeByLocation(calloutByLocData.items)
            : null,
          callOutByLocationPage: calloutByLocData?.meta?.current_page ?? 1,
          callOutByLocationTotalPages: calloutByLocData?.meta?.total_pages ?? 1,
          callOutByLocationLoadingMore: false,
          callOutByLocationError: calloutByLocResult.status === 'rejected',
          activeTeamMembersTotal: toNumber(activeTeamMembersData?.current_period_total),
          activeTeamMembersPeriodDays: activeTeamMembersData?.period_days ?? null,
          activeTeamMembersChangePrct:
            activeTeamMembersData?.change_prct == null
              ? null
              : toNumber(activeTeamMembersData.change_prct),
          activeTeamMembersError: activeTeamMembersResult.status === 'rejected',
          trendData: trendResponseData
            ? fillTrendGaps(mapFulfillmentTrend(trendResponseData), dateRange, trendGranularity)
            : [],
          trendGranularity,
          trendError: trendResult.status === 'rejected',
          isLoading: false,
        });
      });

      return () => {
        fetchId++;
      };
    },
    loadMoreFulfillmentByPosition: () => {
      const {
        fulfillmentByPositionLoadingMore,
        fulfillmentByPositionPage,
        fulfillmentByPositionTotalPages,
      } = get();
      if (
        fulfillmentByPositionLoadingMore ||
        fulfillmentByPositionPage >= fulfillmentByPositionTotalPages ||
        !fulfillmentByPositionBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = fulfillmentByPositionPage + 1;
      set({ fulfillmentByPositionLoadingMore: true });

      fetchDashboardPage<FulfillmentByPositionResponse, FulfillmentByPositionResponseItem>({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_BY_POSITION(),
        query: fulfillmentByPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.fulfillment_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            fulfillmentByPosition: [
              ...(state.fulfillmentByPosition ?? []),
              ...mapFulfillmentByPosition(items),
            ],
            fulfillmentByPositionPage: meta?.current_page ?? nextPage,
            fulfillmentByPositionTotalPages:
              meta?.total_pages ?? state.fulfillmentByPositionTotalPages,
            fulfillmentByPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ fulfillmentByPositionLoadingMore: false });
        });
    },
    loadMoreCallOutByLocation: () => {
      const { callOutByLocationLoadingMore, callOutByLocationPage, callOutByLocationTotalPages } =
        get();
      if (
        callOutByLocationLoadingMore ||
        callOutByLocationPage >= callOutByLocationTotalPages ||
        !callOutByLocationBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = callOutByLocationPage + 1;
      set({ callOutByLocationLoadingMore: true });

      fetchDashboardPage<CalloutVolumeByLocationResponse, CalloutVolumeByLocationResponseItem>({
        url: DASHBOARDS_SHIFTS_CALLOUT_VOLUME_BY_LOCATION(),
        query: callOutByLocationBaseQuery,
        page: nextPage,
        selectItems: (body) => body.callout_volume_by_location ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            callOutByLocation: [
              ...(state.callOutByLocation ?? []),
              ...mapCalloutVolumeByLocation(items),
            ],
            callOutByLocationPage: meta?.current_page ?? nextPage,
            callOutByLocationTotalPages: meta?.total_pages ?? state.callOutByLocationTotalPages,
            callOutByLocationLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ callOutByLocationLoadingMore: false });
        });
    },
    setLoading: (isLoading) => set({ isLoading }),
    reset: () => {
      fetchId = 0;
      fulfillmentByPositionBaseQuery = null;
      callOutByLocationBaseQuery = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useOverviewDashboardStore = overviewDashboardStore;
